import { appInsights } from './appInsightsService'
import { SeverityLevel } from '@microsoft/applicationinsights-web'
import { sanitiseError } from './sanitiseError'

export function logException(message, error) {
  const safeError = sanitiseError(error)
  const errorJson = JSON.stringify(safeError)
  appInsights.trackException({
    error: `${message} ${errorJson}`,
    severityLevel: SeverityLevel.Error,
  })

  if (
    process.env.GATSBY_FEATURE_ENVIRONMENT === 'dev' ||
    process.env.GATSBY_FEATURE_ENVIRONMENT === 'test'
  ) {
    console.log(`ERROR: ${message} ${errorJson}`)
  }
}

// Warning-level logging for expected/degraded states (aborted fetches, crawler
// noise, non-critical blocks failing soft). Uses trackTrace rather than
// trackException so these do not inflate the exceptions table that error
// alerting is built on — the rejection objects carry no useful stack anyway.
export function logWarning(message, error) {
  const safeError = sanitiseError(error)
  const errorJson = JSON.stringify(safeError)
  appInsights.trackTrace({
    message: `${message} ${errorJson}`,
    severityLevel: SeverityLevel.Warning,
  })

  if (
    process.env.GATSBY_FEATURE_ENVIRONMENT === 'dev' ||
    process.env.GATSBY_FEATURE_ENVIRONMENT === 'test'
  ) {
    console.log(`WARNING: ${message} ${errorJson}`)
  }
}

export function logTrace(message) {
  appInsights.trackTrace({
    message: message,
    severityLevel: SeverityLevel.Information,
  })

  if (
    process.env.GATSBY_FEATURE_ENVIRONMENT === 'dev' ||
    process.env.GATSBY_FEATURE_ENVIRONMENT === 'test'
  ) {
    console.log(`INFO: ${message}`)
  }
}

// True when an API-layer rejection never produced an HTTP response: fetch()
// network TypeErrors (page navigations aborting in-flight requests, offline
// clients, blocked requests) and XSRF cookie acquisition failures. These are
// client-environment conditions, not application faults, so callers route them
// to logNetworkFailure (a low-severity, capped trace) instead of logException.
// Rejections that carry an HTTP `status` are real backend errors and return
// false so they stay as exceptions.
//
// Do not special-case `instanceof Error` in here: whoAmI.js:176 passes the nested raw
// fetch TypeError from a wrapped transport rejection, and isNetworkFailure must keep
// returning true for it. Callers that can tell "an Error thrown by their own code" from
// "a rejection object" (basketRecommendations.js, addToBasketModal.js) apply that
// instanceof guard themselves before calling isNetworkFailure.
export function isNetworkFailure(error) {
  if (error === 'failed to get xsrf') {
    return true
  }
  if (typeof error === 'object' && error !== null) {
    return error.status === undefined
  }
  return false
}

// A single broken/offline session can emit dozens of "Failed to fetch" events
// (one per search keystroke, price lookup and basket poll). logNetworkFailure
// records them as Information traces — so they do not feed the exception alert —
// and throttles them: it never logs while the browser reports offline, and caps
// the number of traces per page session. Pure in-memory counter; no I/O.
const NETWORK_FAILURE_LOG_CAP = 8
let networkFailureLogCount = 0

export function logNetworkFailure(context, error) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return
  }
  if (networkFailureLogCount >= NETWORK_FAILURE_LOG_CAP) {
    return
  }
  networkFailureLogCount += 1
  const safeError = sanitiseError(error)
  logTrace(`${context} ${JSON.stringify(safeError)}`)
}
